/**
 * FutureBotWidget — the floating FutureBot bubble + chat panel, available on
 * every screen. Replaces the old BabyBot AIChatbotWidget.
 *
 * Must render INSIDE the Router (it relies on FutureBotProvider, which uses
 * useLocation for page awareness).
 *
 * Preserved behaviors from the old widget: minimize the bubble, hide/restore
 * tab, Ctrl+Shift+C visibility toggle, per-user persistence in localStorage.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Image,
  IconButton,
  Tooltip,
  Badge,
  useColorModeValue,
  useToast,
  useBreakpointValue,
} from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiPlus, FiEyeOff, FiMinus, FiVolume2, FiVolumeX } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { canAccessNewFeatures } from '../../utils/featureAccess';
import { useFutureBotAgent } from './useFutureBotAgent';
import { useSpeechPlayback } from './useSpeechPlayback';
import FutureBotChat from './FutureBotChat';
import astronautIcon from '../../assets/img/astronaut-icon-chat-bubble.png';

const BUTTON_VISIBLE_KEY = 'aiChatWidgetButtonVisible'; // same key as the old widget
const VOICE_MODE_KEY = 'futureBotVoiceMode';
const WIDGET_MARGIN = '20px';

const DEFAULT_SUGGESTIONS = ['What can I do on this page?', 'What can you help me with?'];

const MotionBox = motion(Box);

export default function FutureBotWidget() {
  const { currentUser, SuperAdmin, Roles } = useAuth();
  const toast = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isButtonVisible, setIsButtonVisible] = useState(() => {
    const saved = localStorage.getItem(BUTTON_VISIBLE_KEY);
    return saved ? JSON.parse(saved) : true;
  });

  const { messages, isResponding, currentPage, send, stop, startNewChat, sessionId } =
    useFutureBotAgent();

  // Voice mode: read new FutureBot replies aloud (browser speech synthesis).
  const { supported: speechSupported, speak: speakReply, stop: stopSpeaking } = useSpeechPlayback();
  const [voiceMode, setVoiceMode] = useState(() => localStorage.getItem(VOICE_MODE_KEY) === 'true');
  const spokenIdsRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(VOICE_MODE_KEY, String(voiceMode));
  }, [voiceMode]);

  useEffect(() => {
    // First run: register existing history so reloading never reads old
    // messages aloud. After that, speak each new AI reply while voice mode
    // is on and the panel is open.
    if (spokenIdsRef.current === null) {
      spokenIdsRef.current = new Set(messages.map((m) => m.id));
      return;
    }
    const seen = spokenIdsRef.current;
    for (const msg of messages) {
      if (seen.has(msg.id)) continue;
      seen.add(msg.id);
      if (voiceMode && isOpen && msg.type === 'ai' && msg.text) {
        speakReply(msg.text);
      }
    }
  }, [messages, voiceMode, isOpen, speakReply]);

  // Silence any playback the moment the panel closes.
  useEffect(() => {
    if (!isOpen) stopSpeaking();
  }, [isOpen, stopSpeaking]);

  const handleStop = useCallback(() => {
    stop();
    stopSpeaking();
  }, [stop, stopSpeaking]);

  const toggleVoiceMode = useCallback(() => {
    setVoiceMode((prev) => {
      if (prev) stopSpeaking();
      return !prev;
    });
  }, [stopSpeaking]);

  const panelBg = useColorModeValue('white', 'gray.800');
  const panelBorder = useColorModeValue('gray.200', 'gray.600');
  const headerBg = useColorModeValue('white', 'gray.800');
  const headerBorder = useColorModeValue('gray.100', 'gray.700');
  const restoreBg = useColorModeValue('gray.100', 'gray.700');
  const pageChipBg = useColorModeValue('purple.50', 'whiteAlpha.200');
  const pageChipColor = useColorModeValue('purple.600', 'purple.200');
  const titleColor = useColorModeValue('gray.800', 'white');
  const bubbleSize = useBreakpointValue({ base: 52, md: 72 }) || 72;

  useEffect(() => {
    localStorage.setItem(BUTTON_VISIBLE_KEY, JSON.stringify(isButtonVisible));
  }, [isButtonVisible]);

  // Ctrl+Shift+C toggles bubble visibility (kept from the old widget)
  const handleKeyDown = useCallback(
    (event) => {
      if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 'c') {
        event.preventDefault();
        setIsButtonVisible((prev) => {
          toast({
            title: prev ? 'Chat button hidden' : 'Chat button visible',
            description: prev ? 'Press Ctrl+Shift+C to show it again.' : undefined,
            status: 'info',
            duration: 3000,
            isClosable: true,
          });
          return !prev;
        });
      }
    },
    [toast]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const suggestions = useMemo(() => {
    if (currentPage.suggestions?.length) return currentPage.suggestions;
    return DEFAULT_SUGGESTIONS;
  }, [currentPage.suggestions]);

  // GA: FutureBot is available to every CRM user; hidden from non-CRM Hub users.
  if (!currentUser || !canAccessNewFeatures({ currentUser, SuperAdmin, Roles })) return null;

  return (
    <>
      {/* Floating bubble */}
      {isButtonVisible && !isOpen && (
        <Box
          position="fixed"
          bottom={WIDGET_MARGIN}
          left={{ base: WIDGET_MARGIN, md: 'auto' }}
          right={{ base: 'auto', md: WIDGET_MARGIN }}
          zIndex={9998}
        >
          <Box position="relative">
            <Tooltip label="Minimize" placement="top">
              <IconButton
                icon={<FiMinus size="10px" />}
                isRound
                size="xs"
                boxSize="18px"
                minW="18px"
                position="absolute"
                top="-2px"
                left="-2px"
                zIndex={1}
                bg="gray.400"
                color="white"
                opacity={0.7}
                _hover={{ opacity: 1, bg: 'gray.500' }}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsButtonVisible(false);
                  toast({
                    title: 'Chat button minimized',
                    description: 'Tap the mini chat icon or press Ctrl+Shift+C to restore.',
                    status: 'info',
                    duration: 4000,
                    isClosable: true,
                  });
                }}
                aria-label="Minimize chat bubble"
              />
            </Tooltip>
            <MotionBox
              as="button"
              whileHover={{ scale: 1.06 }}
              whileTap={{ scale: 0.94 }}
              onClick={() => setIsOpen(true)}
              aria-label="Open FutureBot"
              display="block"
              borderRadius="full"
              overflow="hidden"
              boxShadow="0 4px 20px rgba(88, 28, 135, 0.35)"
            >
              <Image src={astronautIcon} boxSize={`${bubbleSize}px`} alt="FutureBot" />
            </MotionBox>
          </Box>
        </Box>
      )}

      {/* Mini restore tab when the bubble is hidden */}
      {!isButtonVisible && !isOpen && (
        <Box
          position="fixed"
          bottom={WIDGET_MARGIN}
          left={{ base: WIDGET_MARGIN, md: 'auto' }}
          right={{ base: 'auto', md: WIDGET_MARGIN }}
          zIndex={9998}
          cursor="pointer"
          onClick={() => setIsButtonVisible(true)}
        >
          <Tooltip label="Show FutureBot" placement="top">
            <Box
              bg={restoreBg}
              borderRadius="md"
              px={2}
              py={1}
              opacity={0.7}
              _hover={{ opacity: 1 }}
              boxShadow="md"
              transition="opacity 0.2s"
            >
              <Image src={astronautIcon} boxSize="20px" alt="Show FutureBot" />
            </Box>
          </Tooltip>
        </Box>
      )}

      {/* Chat panel */}
      <AnimatePresence>
        {isOpen && (
          <MotionBox
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            position="fixed"
            bottom={{ base: WIDGET_MARGIN, md: WIDGET_MARGIN }}
            right={WIDGET_MARGIN}
            top={{ base: WIDGET_MARGIN, md: 'auto' }}
            width={{ base: 'calc(100vw - 40px)', md: '440px' }}
            height={{ base: 'auto', md: '640px' }}
            maxHeight={{ base: 'calc(100vh - 40px)', md: 'calc(100vh - 40px)' }}
            zIndex={9999}
            bg={panelBg}
            borderRadius="2xl"
            border="1px solid"
            borderColor={panelBorder}
            boxShadow="0 12px 48px rgba(0,0,0,0.22)"
            display="flex"
            flexDirection="column"
            overflow="hidden"
          >
            {/* Header */}
            <Flex
              align="center"
              px={4}
              py={3}
              bg={headerBg}
              borderBottom="1px solid"
              borderColor={headerBorder}
              gap={3}
            >
              <Image src={astronautIcon} boxSize="30px" alt="" />
              <Box flex={1} minW={0}>
                <Text fontWeight="bold" fontSize="sm" color={titleColor} lineHeight={1.2}>
                  FutureBot
                </Text>
                <Badge
                  bg={pageChipBg}
                  color={pageChipColor}
                  fontSize="0.6rem"
                  borderRadius="full"
                  px={2}
                  textTransform="none"
                  maxW="100%"
                  isTruncated
                >
                  {currentPage.tools ? `${currentPage.title} · hands-on` : currentPage.title}
                </Badge>
              </Box>
              {speechSupported && (
                <Tooltip label={voiceMode ? 'Voice replies on' : 'Read replies aloud'}>
                  <IconButton
                    icon={voiceMode ? <FiVolume2 /> : <FiVolumeX />}
                    size="sm"
                    variant="ghost"
                    color={voiceMode ? 'purple.400' : undefined}
                    aria-label={voiceMode ? 'Turn off voice replies' : 'Read replies aloud'}
                    onClick={toggleVoiceMode}
                  />
                </Tooltip>
              )}
              <Tooltip label="New chat">
                <IconButton
                  icon={<FiPlus />}
                  size="sm"
                  variant="ghost"
                  aria-label="Start new chat"
                  onClick={() => {
                    stopSpeaking();
                    startNewChat();
                    toast({ title: 'New chat started', status: 'info', duration: 1500 });
                  }}
                />
              </Tooltip>
              <Tooltip label="Hide chat button">
                <IconButton
                  icon={<FiEyeOff />}
                  size="sm"
                  variant="ghost"
                  aria-label="Hide chat button"
                  onClick={() => {
                    setIsButtonVisible(false);
                    setIsOpen(false);
                    toast({
                      title: 'Chat button hidden',
                      description: 'Tap the mini chat icon to show it again.',
                      status: 'info',
                      duration: 4000,
                      isClosable: true,
                    });
                  }}
                />
              </Tooltip>
              <Tooltip label="Close">
                <IconButton
                  icon={<FiX />}
                  size="sm"
                  variant="ghost"
                  aria-label="Close chat"
                  onClick={() => setIsOpen(false)}
                />
              </Tooltip>
            </Flex>

            {/* Conversation */}
            <FutureBotChat
              messages={messages}
              isResponding={isResponding}
              onSend={send}
              onStop={handleStop}
              suggestions={suggestions}
              pageTitle={currentPage.title}
              sessionId={sessionId}
              onSpeak={speechSupported ? speakReply : undefined}
            />
          </MotionBox>
        )}
      </AnimatePresence>
    </>
  );
}
